import { countryCodesByName } from './countryNames';

// Server stand-in: resolves free-text country values (as extracted) to
// country codes for the map. In the real system this would run server-side,
// at publish time.

/** Lower-cases, strips diacritics and normalises apostrophes, so 'Côte d’Ivoire' matches "cote d'ivoire". */
const normalise = (text: string) =>
    text.normalize('NFD').replace(/\p{M}/gu, '').replace(/[’‘`]/g, "'").toLowerCase();

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const codesByNormalisedName = new Map(
    Object.entries(countryCodesByName).map(([name, code]) => [normalise(name), code])
);

// Longest names first, so at any position the alternation prefers e.g.
// 'south sudan' over 'sudan' and 'papua new guinea' over 'guinea'. Names must
// stand alone as words, so 'nigeria' never matches 'niger'.
const countryNamePattern = new RegExp(
    `(?<![\\p{L}\\p{N}])(${[...codesByNormalisedName.keys()]
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp)
        .join('|')})(?![\\p{L}\\p{N}])`,
    'gu'
);

/**
 * Every country named in a free-text value, in order of first appearance and
 * without duplicates — e.g. 'Morocco, Algeria, Tunisia and Libya' gives four
 * codes, 'eastern Kenya' and 'Kenya; Kenya' give one. Returns [] when no
 * country is recognised (e.g. 'eastern Africa').
 */
export const resolveCountryCodes = (value: string | null | undefined): string[] => {
    if (!value) return [];
    const codes = new Set<string>();
    for (const match of normalise(value).matchAll(countryNamePattern)) {
        const code = codesByNormalisedName.get(match[1]);
        if (code) codes.add(code);
    }
    return [...codes];
};
